import 'react-phone-number-input/style.css';

import {
    Button,
    CommonProps,
    Form,
    Heading,
    InputField,
    Logo,
    Text,
} from '@beat-venues/ui/react';
import { cva, VariantProps } from 'class-variance-authority';
import { Dispatch, FormEvent, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import PhoneInput from 'react-phone-number-input';
import { twMerge } from 'tailwind-merge';

import { ActiveStep, NewUserDetail, ViewStateProps } from '../../pages/welcome';

export const ViewStepStyles = cva('', {
    variants: {
        stepBackground: {
            welcome: 'bg-[url(/assets/welcome-flow/bg-welcome.webp)]',
            firstName: 'bg-[url(/assets/welcome-flow/bg-firstName.webp)]',
            lastName: 'bg-[url(/assets/welcome-flow/bg-lastName.webp)]',
            phone: 'bg-[url(/assets/welcome-flow/bg-phone.webp)]',
        },
    },
    defaultVariants: {
        stepBackground: 'welcome',
    },
});

export interface ViewStepProps
    extends CommonProps,
        VariantProps<typeof ViewStepStyles> {
    view: ViewStateProps;
    setView: Dispatch<SetStateAction<ViewStateProps>>;
    userDetails: NewUserDetail;
    setUserDetails: Dispatch<SetStateAction<NewUserDetail>>;
    activeStep: ActiveStep;
    isPhoneValid: boolean;
    handleSubmit?: (e: FormEvent) => void;
    title?: string | null;
    subTitle?: string | null;
}

export const ViewStep = ({
    view,
    setView,
    userDetails,
    setUserDetails,
    className,
    activeStep,
    isPhoneValid,
    stepBackground,
    handleSubmit,
    title,
    subTitle,
}: ViewStepProps) => {
    const { t } = useTranslation('');
    const isValidMobilePhone = isPhoneValid;
    return (
        <div className={twMerge(ViewStepStyles({ stepBackground, className }))}>
            <Logo
                className="mt-8 h-[66px] lg:mt-2 xl:h-[100px]"
                venueId="my-beat"
            />
            <div className="mb-[10vh] lg:mb-0">
                <div>
                    {title && <Heading as="h2">{title}</Heading>}
                    {subTitle && <Text size="xl">{subTitle}</Text>}
                </div>
                <Form
                    className="w-full"
                    onSubmit={
                        handleSubmit
                            ? handleSubmit
                            : (e) => {
                                  e.preventDefault();
                                  setView({
                                      ...view,
                                      [activeStep]: false,
                                      [Object.keys(view)[
                                          Object.keys(view).indexOf(
                                              activeStep
                                          ) + 1
                                      ]]: true,
                                  });
                              }
                    }
                >
                    {activeStep === 'phone' ? (
                        <div className="mt-6">
                            <label className="sr-only" htmlFor="mobilePhone">
                                {t(`userAccount.mobilePhone`)}
                            </label>
                            <PhoneInput
                                international
                                className="react-international-phone-input-container"
                                defaultCountry="BE"
                                id="mobilePhone"
                                name="mobilePhone"
                                onChange={(newValue) => {
                                    setUserDetails({
                                        ...userDetails,
                                        mobilePhone: newValue,
                                    });
                                }}
                            />
                            {!isValidMobilePhone && (
                                <div className="text-la-brand-500 -mt-3 mb-6 pl-5">
                                    {t('messages.invalidMobile')}
                                </div>
                            )}
                        </div>
                    ) : (
                        <>
                            {activeStep === 'firstName' && (
                                <InputField
                                    autoFocus
                                    required
                                    className="my-6"
                                    hideLabel={true}
                                    label={t('userAccount.firstName') || ''}
                                    maxLength={60}
                                    name="firstName"
                                    pattern="^(?=.*\S).*$"
                                    placeholder={
                                        t('userAccount.firstName') || ''
                                    }
                                    onChange={(newValue) =>
                                        setUserDetails({
                                            ...userDetails,
                                            [activeStep]: newValue,
                                        })
                                    }
                                />
                            )}
                            {activeStep === 'lastName' && (
                                <InputField
                                    autoFocus
                                    required
                                    className="my-6"
                                    hideLabel={true}
                                    label={t('userAccount.lastName') || ''}
                                    maxLength={60}
                                    name="lastName"
                                    pattern="^(?=.*\S).*$"
                                    placeholder={
                                        t('userAccount.lastName') || ''
                                    }
                                    onChange={(newValue) =>
                                        setUserDetails({
                                            ...userDetails,
                                            [activeStep]: newValue,
                                        })
                                    }
                                />
                            )}
                        </>
                    )}
                    <div className="flex items-center justify-center space-x-2 py-6">
                        {view &&
                            Object.entries(view).length > 0 &&
                            Object.entries(view)
                                .filter(([key]) => key !== 'success')
                                .map(([key, value]) => (
                                    <div
                                        key={key}
                                        className={`h-3 w-3 rounded-full transition-colors ${
                                            value
                                                ? 'h-3.5 w-3.5 bg-white'
                                                : 'bg-white/50'
                                        }`}
                                    />
                                ))}
                    </div>
                    <Button className="w-full" type="submit">
                        {t('welcome.button')}
                    </Button>
                </Form>
            </div>
        </div>
    );
};
